// src/app/features/markets/marketCatalog.js

/**
 * Unified market catalog using Binance symbols for:
 * - CRYPTO: BTCUSDT, ETHUSDT, etc.
 * - FX: fiat/USDT spot pairs where Binance offers them (EURUSDT, GBPUSDT, ...)
 * - METALS: gold proxy via PAXGUSDT (PAX Gold)
 *
 * Compatibility note:
 * marketContext.jsx imports getMarketById — so we export it.
 */

export const ASSET_CLASSES = Object.freeze({
  CRYPTO: "CRYPTO",
  FX: "FX",
  METALS: "METALS",
});

// Simple icon glyphs used in your MarketButton UI
const ICONS = {
  BTC: "₿",
  ETH: "Ξ",
  SOL: "◎",
  BNB: "⬡",
  XRP: "✕",
  ADA: "A",
  DOGE: "Ð",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  JPY: "¥",
  CAD: "C$",
  CHF: "CHF",
  PAXG: "Au",
};

function m({
  id,
  label,
  base,
  quote = "USDT",
  icon,
  provider = "binance",
  symbol, // explicit provider symbol; fallback = id
}) {
  return {
    id,
    label,
    base,
    quote,
    icon,
    provider,
    symbol: symbol || id,
  };
}

/**
 * Primary catalog. Keep IDs aligned with Binance symbols to avoid extra mapping.
 */
export const MARKET_CATALOG = Object.freeze({
  [ASSET_CLASSES.CRYPTO]: [
    m({ id: "BTCUSDT", label: "Bitcoin", base: "BTC", icon: ICONS.BTC }),
    m({ id: "ETHUSDT", label: "Ethereum", base: "ETH", icon: ICONS.ETH }),
    m({ id: "SOLUSDT", label: "Solana", base: "SOL", icon: ICONS.SOL }),
    m({ id: "BNBUSDT", label: "BNB", base: "BNB", icon: ICONS.BNB }),
    m({ id: "XRPUSDT", label: "XRP", base: "XRP", icon: ICONS.XRP }),
    m({ id: "ADAUSDT", label: "Cardano", base: "ADA", icon: ICONS.ADA }),
    m({ id: "DOGEUSDT", label: "Dogecoin", base: "DOGE", icon: ICONS.DOGE }),
  ],

  // FX via Binance fiat/USDT spot markets (when available on Binance)
  [ASSET_CLASSES.FX]: [
    m({ id: "EURUSDT", label: "Euro", base: "EUR", icon: ICONS.EUR }),
    m({ id: "GBPUSDT", label: "British Pound", base: "GBP", icon: ICONS.GBP }),
    m({ id: "AUDUSDT", label: "Australian Dollar", base: "AUD", icon: ICONS.AUD }),
    m({ id: "JPYUSDT", label: "Japanese Yen", base: "JPY", icon: ICONS.JPY }),
    m({ id: "CADUSDT", label: "Canadian Dollar", base: "CAD", icon: ICONS.CAD }),
    m({ id: "CHFUSDT", label: "Swiss Franc", base: "CHF", icon: ICONS.CHF }),
  ],

  // Metals proxy: PAXG is gold-backed and traded as a live market (proxy for Gold)
  [ASSET_CLASSES.METALS]: [
    m({ id: "PAXGUSDT", label: "Gold (PAXG)", base: "PAXG", icon: ICONS.PAXG }),
  ],
});

export const DEFAULT_MARKET_BY_CLASS = Object.freeze({
  [ASSET_CLASSES.CRYPTO]: "BTCUSDT",
  [ASSET_CLASSES.FX]: "EURUSDT",
  [ASSET_CLASSES.METALS]: "PAXGUSDT",
});

export function getMarketsForAssetClass(assetClass) {
  return MARKET_CATALOG[assetClass] || [];
}

export function findMarket(assetClass, marketId) {
  const list = getMarketsForAssetClass(assetClass);
  return list.find((x) => x.id === marketId) || null;
}

/**
 * Compatibility: used by marketContext.jsx
 * Looks up a market by ID across ALL asset classes.
 */
export function getMarketById(marketId) {
  const id = String(marketId || "").toUpperCase();
  for (const cls of Object.values(ASSET_CLASSES)) {
    const found = (MARKET_CATALOG[cls] || []).find((m) => String(m.id).toUpperCase() === id);
    if (found) return found;
  }
  return null;
}

/**
 * Optional additional compatibility helpers (harmless if unused)
 */
export function getDefaultMarketId(assetClass) {
  return DEFAULT_MARKET_BY_CLASS[assetClass] || DEFAULT_MARKET_BY_CLASS[ASSET_CLASSES.CRYPTO];
}

export function getAllMarkets() {
  return Object.values(ASSET_CLASSES).flatMap((cls) => MARKET_CATALOG[cls] || []);
}
